import { Bubble, ButtonLoading, TabContent } from '@shared/components';
import { TFile, TFileVersion } from '../types';
import { Body1Strong } from '@fluentui/react-components';
import { ArrowDownloadFilled, OpenFilled } from '@fluentui/react-icons';
import classNames from 'classnames';

import DownloadButton from './DownloadButton';
import { TReplica } from '@modules/replicas';
import { EHandlePdfBlobMode, ITEM_MIMETYPES, FileIcon, getPdfFilename, handlePdfBlob } from '@modules/items';
import { TPermissions } from '@modules/access';
import React, { useCallback, useMemo, useState } from 'react';
import { ApprovalStatus } from '@modules/approvals';
import { useSignFileVersion } from '../api';
import { blobHref } from '@shared/utils';
import getStaticFileDownloadFilename from '../helpers/getStaticFileDownloadFilename.helper';

type TProps = {
  file: TFile;
  version: TFileVersion;
  permissions: TPermissions;
  replica?: TReplica;
};

const Content = ({ file, version, permissions, replica }: TProps) => {
  // Basic booleans to determine which UI actions to show
  const isPdf = ITEM_MIMETYPES.pdf.includes(version.mimetype);
  const isImage = ITEM_MIMETYPES.image.includes(version.mimetype);
  const originalFilename = getStaticFileDownloadFilename((replica || file).name, version.blobName);
  const pdfFilename = getPdfFilename((replica || file).name, file.pdfExportFileNames?.[version.id]);

  // Check if version is approved
  const isApproved = useMemo(() => {
    return file.approvals.some((a) => a.versionId === version.id && a.status === ApprovalStatus.APPROVED);
  }, [file, version.id]);

  // The signVersion mutation used to create a PDF if the version isn't yet signed
  const signFileVersionMutation = useSignFileVersion();

  const handleSigned = useCallback(async () => {
    const onSuccess = (updatedVersion: TFileVersion) => {
      const blobName = updatedVersion.pdfBlobName || updatedVersion.blobName;
      const filename = updatedVersion.pdfBlobName ? pdfFilename : (replica || file).name;
      handlePdfBlob(file._id, blobName, permissions.token, EHandlePdfBlobMode.DOWNLOAD, filename);
    };

    if (isApproved && !version.signature)
      // sign now:
      await signFileVersionMutation.mutateAsync({ id: file._id, versionId: version.id }, { onSuccess }); // already signed:
    else onSuccess(version);
  }, [file._id, file.name, replica, pdfFilename, isApproved, permissions.token, version]);

  // Build up the list of action buttons
  const actionButtons: React.ReactNode[] = [];

  // Only if it's a PDF, show the "Open" button
  if (isPdf) {
    if (!isApproved || version.signature)
      // either not approved or already signed
      actionButtons.push(
        <DownloadButton
          key="download-pdf-variant"
          href={blobHref(file._id, version.pdfBlobName || version.blobName, permissions.token)}
          openInBrowser
          mimetype={ITEM_MIMETYPES.pdf[0]}
          filename={pdfFilename}
          icon={<OpenFilled />}
          size="large"
          appearance="primary"
        >
          {`View ${isApproved ? 'signed' : ''}`}
        </DownloadButton>
      );
    // If not a PDF, but has a PDF variant and is not yet signed, show the "Download PDF variant" button
  } else if (version.pdfBlobName && !isApproved) {
    actionButtons.push(
      <DownloadButton
        key="download-pdf-variant"
        href={blobHref(file._id, version.pdfBlobName, permissions.token)}
        mimetype={ITEM_MIMETYPES.pdf[0]}
        filename={pdfFilename}
        icon={<ArrowDownloadFilled />}
        size="large"
        appearance="primary"
      >
        Download PDF variant
      </DownloadButton>
    );
  }

  /**
   * For all custom file types, show the "Download signed" button if the file is approved.
   * Notice, the file might be signed on the fly, so we don't care whether `version.signature` is set or not.
   */
  if (isApproved) {
    actionButtons.push(
      <ButtonLoading
        as="a"
        key="download-signed"
        onClick={handleSigned}
        loading={signFileVersionMutation.isPending}
        icon={<ArrowDownloadFilled />}
        size="large"
        // If the file is a PDF, use the secondary appearance because the "Open" button is primary
        appearance={isPdf ? 'secondary' : 'primary'}
      >
        Download signed{!isPdf ? ' PDF' : ''}
      </ButtonLoading>
    );
  }

  // Always show the "Download original" button
  actionButtons.push(
    <DownloadButton
      key="download-original"
      href={blobHref(file._id, version.blobName, permissions.token)}
      mimetype={version.mimetype}
      filename={originalFilename}
      icon={<ArrowDownloadFilled />}
      size="large"
      appearance="secondary"
    >
      Download original
    </DownloadButton>
  );

  return (
    <TabContent>
      <Bubble>
        <div
          className={classNames('flex flex-col items-center space-y-4', {
            'my-4 pb-2': isImage,
            'my-12 pb-2': !isImage,
          })}
        >
          {/* If it's not an image, show a file icon + version */}
          {!isImage && (
            <div className="flex flex-col items-center space-y-0.5">
              <div className="w-28 h-28">
                <FileIcon
                  type={replica ? replica.type : 'FILE'}
                  path={file.resolvedPath.map((pathItem) => pathItem._id)}
                  size="auto"
                  isBlock={false}
                  isFrozen={file.isFrozen}
                  isTemplate={false}
                  mimetype={version.mimetype}
                  name={file.name}
                />
              </div>
              <Body1Strong className="text-neutral-600">Version {version.id}</Body1Strong>
            </div>
          )}

          {/* Render action buttons */}
          <div className="flex space-x-3">{actionButtons}</div>

          {/* If it's an image, show a preview */}
          {isImage && (
            <div>
              <img src={blobHref(file._id, version.blobName, permissions.token)} alt="Image cannot be displayed." />
            </div>
          )}
        </div>
      </Bubble>
    </TabContent>
  );
};

export default Content;
